import React, { useState } from 'react';
import {
  Package, Search, Filter, ChevronDown, Eye, Split, CheckCircle,
  AlertTriangle, Users, ArrowRight, RefreshCw
} from 'lucide-react';

const kplMixBags = [
  {
    id: 'KPL-MIX-2026-045',
    manifest: 'MAN-2026-02-28-001',
    totalItems: 15,
    totalWeight: '42.5 lb',
    status: 'pending',
    couriers: [
      { prefix: 'FS', name: 'FastShip', items: 6, weight: '18.2 lb' },
      { prefix: 'SC', name: 'SpeedyCourier', items: 5, weight: '14.1 lb' },
      { prefix: 'IE', name: 'IslandExpress', items: 4, weight: '10.2 lb' },
    ],
    date: '2026-02-28'
  },
  {
    id: 'KPL-MIX-2026-044',
    manifest: 'MAN-2026-02-27-003',
    totalItems: 8,
    totalWeight: '28.3 lb',
    status: 'split',
    couriers: [
      { prefix: 'FS', name: 'FastShip', items: 3, weight: '10.5 lb' },
      { prefix: 'KP', name: 'KP Direct', items: 5, weight: '17.8 lb' },
    ],
    date: '2026-02-27'
  },
  {
    id: 'KPL-MIX-2026-043',
    manifest: 'MAN-2026-02-26-001',
    totalItems: 22,
    totalWeight: '65.8 lb',
    status: 'split',
    couriers: [
      { prefix: 'FS', name: 'FastShip', items: 8, weight: '24.2 lb' },
      { prefix: 'SC', name: 'SpeedyCourier', items: 7, weight: '21.4 lb' },
      { prefix: 'IE', name: 'IslandExpress', items: 4, weight: '12.1 lb' },
      { prefix: 'KP', name: 'KP Direct', items: 3, weight: '8.1 lb' },
    ],
    date: '2026-02-26'
  },
];

const courierColors = {
  FS: '#3b82f6',
  SC: '#06b6d4',
  IE: '#84cc16',
  KP: '#f59e0b'
};

export default function KPLMix() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBag, setSelectedBag] = useState(null);
  const [splitting, setSplitting] = useState(false);

  const filteredBags = kplMixBags.filter(bag => {
    const matchesSearch = bag.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || bag.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleSplit = (bag) => {
    setSplitting(true);
    setTimeout(() => {
      setSplitting(false);
      setSelectedBag(null);
    }, 2000);
  };

  const pendingCount = kplMixBags.filter(b => b.status === 'pending').length;
  const splitCount = kplMixBags.filter(b => b.status === 'split').length;

  return (
    <div className="kpl-mix-page" data-testid="kpl-mix-page">
      {/* Summary Stats */}
      <div className="kpl-summary">
        <div className="kpl-stat pending">
          <AlertTriangle size={20} />
          <span className="stat-value">{pendingCount}</span>
          <span className="stat-label">Pending Split</span>
        </div>
        <div className="kpl-stat split">
          <CheckCircle size={20} />
          <span className="stat-value">{splitCount}</span>
          <span className="stat-label">Already Split</span>
        </div>
        <div className="kpl-stat total">
          <Package size={20} />
          <span className="stat-value">{kplMixBags.length}</span>
          <span className="stat-label">Total KPL Mix</span>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <div className="info-icon">
          <Package size={20} />
        </div>
        <div className="info-content">
          <h4>What is KPL Mix?</h4>
          <p>KPL Mix bags contain packages from multiple couriers consolidated into a single bag. These need to be split so each courier receives their own invoice.</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search KPL Mix bags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="kpl-search"
          />
        </div>
        <div className="toolbar-right">
          <select 
            className="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="kpl-status-filter"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="split">Split</option>
          </select>
        </div>
      </div>

      {/* KPL Mix Cards */}
      <div className="kpl-cards-grid">
        {filteredBags.map((bag) => (
          <div 
            key={bag.id} 
            className={`kpl-card ${bag.status}`}
            data-testid={`kpl-card-${bag.id}`}
          >
            <div className="kpl-card-header">
              <div className="kpl-id-section">
                <span className="kpl-id">{bag.id}</span>
                <span className={`kpl-status ${bag.status}`}>
                  {bag.status === 'pending' ? <AlertTriangle size={12} /> : <CheckCircle size={12} />}
                  {bag.status}
                </span>
              </div>
              <span className="kpl-date">{bag.date}</span>
            </div>

            <div className="kpl-card-body">
              <div className="kpl-info-row">
                <span className="info-label">Manifest:</span>
                <span className="info-value">{bag.manifest}</span>
              </div>
              <div className="kpl-info-row">
                <span className="info-label">Total Items:</span>
                <span className="info-value">{bag.totalItems}</span>
              </div>
              <div className="kpl-info-row">
                <span className="info-label">Total Weight:</span>
                <span className="info-value">{bag.totalWeight}</span>
              </div>

              <div className="courier-breakdown">
                <span className="breakdown-label">
                  <Users size={14} /> Courier Breakdown
                </span>
                <div className="courier-bars">
                  {bag.couriers.map((courier, idx) => (
                    <div 
                      key={courier.prefix}
                      className="courier-bar"
                      style={{
                        flex: courier.items,
                        backgroundColor: courierColors[courier.prefix]
                      }}
                      title={`${courier.name}: ${courier.items} items`}
                    >
                      {courier.prefix}
                    </div>
                  ))}
                </div>
                <div className="courier-details">
                  {bag.couriers.map((courier) => (
                    <div key={courier.prefix} className="courier-detail-item">
                      <span 
                        className="courier-dot" 
                        style={{ backgroundColor: courierColors[courier.prefix] }}
                      ></span>
                      <span className="courier-name">{courier.name}</span>
                      <span className="courier-count">{courier.items} items</span>
                      <span className="courier-weight">{courier.weight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="kpl-card-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedBag(bag)}
                data-testid={`view-${bag.id}`}
              >
                <Eye size={14} /> View Details
              </button>
              {bag.status === 'pending' && (
                <button 
                  className="btn btn-primary"
                  onClick={() => setSelectedBag(bag)}
                  data-testid={`split-${bag.id}`}
                >
                  <Split size={14} /> Split & Invoice
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Split Detail Modal */}
      {selectedBag && (
        <div className="modal-overlay" onClick={() => setSelectedBag(null)}>
          <div className="modal-content kpl-modal" onClick={e => e.stopPropagation()} data-testid="kpl-detail-modal">
            <div className="modal-header">
              <div>
                <h3>{selectedBag.id}</h3>
                <span className="modal-subtitle">{selectedBag.manifest}</span>
              </div>
              <button className="close-btn" onClick={() => setSelectedBag(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="split-preview">
                <div className="split-source">
                  <h4>KPL Mix Bag</h4>
                  <div className="split-box source">
                    <Package size={32} />
                    <span className="box-label">{selectedBag.totalItems} items</span>
                    <span className="box-weight">{selectedBag.totalWeight}</span>
                  </div>
                </div>
                
                <div className="split-arrow">
                  <ArrowRight size={24} />
                  <span>Split</span>
                </div>
                
                <div className="split-targets">
                  <h4>Individual Invoices</h4>
                  <div className="target-boxes">
                    {selectedBag.couriers.map((courier) => (
                      <div 
                        key={courier.prefix}
                        className="split-box target"
                        style={{ borderColor: courierColors[courier.prefix] }}
                      >
                        <span 
                          className="courier-badge"
                          style={{ backgroundColor: courierColors[courier.prefix] }}
                        >
                          {courier.prefix}
                        </span>
                        <span className="box-label">{courier.name}</span>
                        <span className="box-items">{courier.items} items</span>
                        <span className="box-weight">{courier.weight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="items-table-section">
                <h4>Package Details</h4>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Tracking #</th>
                      <th>Courier</th>
                      <th>Weight</th>
                      <th>Est. Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBag.couriers.flatMap((courier, ci) => 
                      Array.from({ length: courier.items }, (_, i) => (
                        <tr key={`${courier.prefix}-${i}`}>
                          <td>{courier.prefix}-PKG-{String(ci * 10 + i + 1).padStart(3, '0')}</td>
                          <td>
                            <span 
                              className="courier-badge small"
                              style={{ backgroundColor: courierColors[courier.prefix] }}
                            >
                              {courier.name}
                            </span>
                          </td>
                          <td>{(parseFloat(courier.weight) / courier.items).toFixed(1)} lb</td>
                          <td>$3,148</td>
                        </tr>
                      ))
                    ).slice(0, 10)}
                  </tbody>
                </table>
                {selectedBag.totalItems > 10 && (
                  <p className="table-note">Showing 10 of {selectedBag.totalItems} items</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedBag(null)}>
                Cancel
              </button>
              {selectedBag.status === 'pending' && (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSplit(selectedBag)}
                  disabled={splitting}
                  data-testid="confirm-split-btn"
                >
                  {splitting ? (
                    <>
                      <RefreshCw size={14} className="spinning" /> Processing...
                    </>
                  ) : (
                    <>
                      <Split size={14} /> Confirm Split & Generate Invoices
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
